import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import Sidebar from './layout/Sidebar';
import Dashboard from './sections/Dashboard';
import Parents from './sections/Parents';
import Coaches from './sections/Coaches';
import Rosters from './sections/Rosters';
import Messages from './sections/Messages';
import { Locations } from './sections/Locations';
import { Events } from './sections/Events';
// COMMENTED OUT: GameSchedule, Community, OrganizationManagement, MatchCreation
// import GameSchedule from './sections/GameSchedule';
// import CommunityManagement from './sections/community/Community';
// import CommunityFeed from './sections/community/CommunityFeed';
import Members from './sections/Members';
import AdminManagement from './sections/AdminManagement';
import IDManagement from './sections/IDManagement';
import Resource from './sections/Resource';
import Curriculum from './sections/Curriculum';
import UniformManagement from './sections/UniformManagement';
import Agreements from './sections/Agreements';
// import OrganizationManagement from './sections/OrganizationManagement';
// import MatchCreation from './sections/community/MatchCreation';
// import Timesheet from './sections/Timesheet';
import AdminTimesheetDashboard from './sections/AdminTimesheetDashboard';
import CoachTimesheetsPage from './sections/CoachTimesheetsPage';
import Payroll from './sections/Payroll';
import PickupAdminSchools from './sections/PickupAdminSchools';
import PickupAdminSchoolDetail from './sections/PickupAdminSchoolDetail';
import PickupAdminEnrollments from './sections/PickupAdminEnrollments';
import PickupAdminEnrollmentDetail from './sections/PickupAdminEnrollmentDetail';
import SchoolRosters from './sections/SchoolRosters';
// import EmailMessaging from './sections/EmailMessaging';
import { useAuth } from '../context/AuthContext';

const PickupAdminSchoolDetailRedirect = () => {
  const { schoolId } = useParams();
  return <Navigate to={`/admin/schools-pickup/${schoolId}`} replace />;
};

// Map route paths to sidebar tab IDs
const routeTabMap = {
  'dashboard': 'dashboard',
  'timeSheet': 'timesheet',
  'coach-timesheets': 'timesheet',
  'admin-management': 'admins',
  'parents': 'members',
  'members': 'members',
  'id-management': 'id_management',
  'resources': 'resources',
  'curriculum': 'curriculum',
  'uniform-management': 'uniform-management',
  'schools-pickup': 'schools-pickup',
  'school-rosters': 'school-rosters',
  'agreements': 'agreements',
  'coaches': 'coaches',
  'rosters': 'rosters',
  'messages': 'messages',
  'locations': 'locations',
  'events': 'events',
  'payroll': 'payroll',
  'schedule': 'schedule',
  'community': 'community',
  'community-feed': 'community',
  'external_schedules': 'external_schedules',
  'match': 'external_schedules',
  'team-management': 'schedule',
  'pickup-admin': 'schools-pickup',
};

const ProtectedTabRoute = ({ children, tabId }) => {
  const { user } = useAuth();
  // Always allow agreements — new feature
  if (tabId === 'agreements') return children;
  // If adminPermissions is set, check access
  if (user?.adminPermissions && Array.isArray(user.adminPermissions) && user.adminPermissions.length > 0) {
    if (user.adminPermissions.includes(tabId)) return children;
    return <Navigate to="/admin/dashboard" replace />;
  }
  // Fallback: allow (backwards compatibility)
  return children;
};

const AdminPanel = () => {
  const location = useLocation();

  // Extract current section from URL
  const getCurrentSection = () => {
    const path = location.pathname.split('/')[2];
    return path || 'dashboard';
  };

  const activeSection = getCurrentSection();

  const renderProtectedRoute = (path, element, tabId) => {
    const resolvedTabId = tabId || routeTabMap[path.split('/')[0]] || path.split('/')[0];
    return (
      <Route
        path={path}
        element={<ProtectedTabRoute tabId={resolvedTabId}>{element}</ProtectedTabRoute>}
      />
    );
  };

  return (
    <div className="gradient-bg h-screen overflow-hidden">
      <div className="flex h-full">
        {/* Fixed Sidebar */}
        <Sidebar activeSection={activeSection} />

        {/* Main Content Area - Only this scrolls */}
        <main className="flex-1 flex flex-col min-w-0 h-full overflow-hidden">
          <div className="flex-1 overflow-y-auto">
            <div className="p-4 lg:p-8">
              <Routes>
                <Route path="/" element={<Navigate to="/admin/dashboard" replace />} />
                {renderProtectedRoute('/dashboard', <Dashboard />, 'dashboard')}
                {renderProtectedRoute('/timeSheet', <AdminTimesheetDashboard />, 'timesheet')}
                {renderProtectedRoute('/coach-timesheets/:coachId', <CoachTimesheetsPage />, 'timesheet')}
                {renderProtectedRoute('/admin-management', <AdminManagement />, 'admins')}
                {renderProtectedRoute('/parents', <Parents />, 'members')}
                {renderProtectedRoute('/members', <Members />, 'members')}
                {renderProtectedRoute('/id-management', <IDManagement />, 'id_management')}
                {renderProtectedRoute('/resources', <Resource />, 'resources')}
                {renderProtectedRoute('/curriculum', <Curriculum />, 'curriculum')}
                {renderProtectedRoute('/uniform-management', <UniformManagement />, 'uniform-management')}
                {renderProtectedRoute('/schools-pickup', <PickupAdminSchools />, 'schools-pickup')}
                {renderProtectedRoute('/schools-pickup/enrollments', <PickupAdminEnrollments />, 'schools-pickup')}
                {renderProtectedRoute('/schools-pickup/enrollments/:enrollmentId', <PickupAdminEnrollmentDetail />, 'schools-pickup')}
                {renderProtectedRoute('/schools-pickup/:schoolId', <PickupAdminSchoolDetail />, 'schools-pickup')}
                {renderProtectedRoute('/school-rosters', <SchoolRosters />, 'school-rosters')}
                {/* Backwards-compatible redirects */}
                <Route path="/pickup-admin/schools" element={<Navigate to="/admin/schools-pickup" replace />} />
                <Route path="/pickup-admin/schools/:schoolId" element={<PickupAdminSchoolDetailRedirect />} />
                {renderProtectedRoute('/agreements', <Agreements />, 'agreements')}
                {renderProtectedRoute('/coaches', <Coaches />, 'coaches')}
                {renderProtectedRoute('/rosters', <Rosters />, 'rosters')}
                <Route path="/coach-assignments" element={<Navigate to="/admin/coaches" replace />} />
                {renderProtectedRoute('/messages', <Messages />, 'messages')}
                {/* <Route path="/email-messaging" element={<EmailMessaging />} /> */}
                {renderProtectedRoute('/locations', <Locations />, 'locations')}
                {renderProtectedRoute('/events', <Events />, 'events')}
                {renderProtectedRoute('/payroll', <Payroll />, 'payroll')}
                {/* COMMENTED OUT: Game Schedule, Community, Organization Management */}
                {/* {renderProtectedRoute('/schedule', <GameSchedule />, 'schedule')} */}
                {/* <Route path="/team-management" element={<Navigate to="/admin/schedule" replace />} /> */}
                {/* {renderProtectedRoute('/community', <CommunityManagement />, 'community')} */}
                {/* {renderProtectedRoute('/community-feed', <CommunityFeed />, 'community')} */}
                {/* {renderProtectedRoute('/external_schedules', <OrganizationManagement />, 'external_schedules')} */}
                {/* {renderProtectedRoute('/match', <MatchCreation />, 'external_schedules')} */}
                <Route path="*" element={<Navigate to="/admin/dashboard" replace />} />
              </Routes>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminPanel;